import React, {useContext, useEffect, useState} from "react";
import SimpleDialog from "../dialog/SimpleDialog";
import {LanguageContext} from "../../context/LanguageContext";
import {Box, Typography} from "@mui/material";
import {saveMfaStatus} from "../../api/contactResource";
import {UserContext} from "../../context/UserContext";
import {useRequestErrorMessage} from "../../utils/useRequestErrorMessage";
import {useSnackbar} from "notistack";
import Button from "@mui/material/Button";
import {CustomerContext} from "../../context/CustomerContext";
import {useAuth} from "oidc-react";
import LogoutDialog from "./LogoutDialog";

const oneDayInMs = 24 * 60 * 60 * 1000;

const MfaDialog = () => {
  const auth = useAuth();
  const userContext = useContext(UserContext);
  const customerContext = useContext(CustomerContext);
  const languageContext = useContext(LanguageContext);
  const {enqueueSnackbar} = useSnackbar();
  const {showRequestError} = useRequestErrorMessage();
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const cookieName = `mfa_reminder_${userContext?.userInfo?.email}`;

  useEffect(() => {
    if (userContext?.userInfo?.email && customerContext?.selectedContact?.roles?.includes("DNS_TEMP")) {
      const lastVisit = getCookie(cookieName);
      const timeSinceLastVisit = Date.now() - new Date(lastVisit).getTime();
      if (!lastVisit || timeSinceLastVisit >= oneDayInMs) {
        setMfaDialogOpen(true);
      }
    }
  }, [customerContext?.selectedContact?.roles, userContext?.userInfo?.email]);

  const handleApiCall = (accept) => {
    setLoading(true);
    saveMfaStatus(auth.userData.access_token, accept)
      .then(() => {
        let message = accept ? "successfulAcceptance" : "successfulRejection";
        resetReminder();
        enqueueSnackbar(languageContext.dictionary[message], {variant: "success", preventDuplicate: true});
        if (accept) {
          setLogoutDialogOpen(true);
        }
      })
      .catch(error => showRequestError(error, languageContext.dictionary.errorMessages[accept ? "acceptError" : "rejectError"]))
      .finally(() => {
        setMfaDialogOpen(false);
        setLoading(false);
      });
  };

  const handleCancel = () => {
    resetReminder();
    setMfaDialogOpen(false);
  };

  const resetReminder = () => {
    eraseCookie();
    set24HourCookie(new Date().toISOString());
  };

  const set24HourCookie = (value) => setCookie(value, 1);

  return (
    <div style={{zIndex: 2000}}>
      <SimpleDialog
        handleClose={handleCancel}
        open={mfaDialogOpen}
        title={languageContext.dictionary.notice}
        blocking
        newDesign
        maxWidth={"md"}
        loading={loading}
        buttonProps={{
          ok: {title: languageContext.dictionary.accept, onClick: () => handleApiCall(true)},
          cancel: {title: languageContext.dictionary.reject, color: "error", onClick: () => handleApiCall(false)},
          close: {title: languageContext.dictionary.later}
        }}
      >
        <Typography sx={{fontWeight: "bold", paddingBottom: 1}}>
          {languageContext.dictionary.mfaDialogContent.text}
        </Typography>
        <Box sx={{paddingLeft: 1}}>
          <Typography sx={{fontWeight: "bold", paddingBottom: 1}}>
            {languageContext.dictionary.mfaDialogContent.firstPoint}
          </Typography>
          <Typography sx={{fontWeight: "bold"}}>
            {languageContext.dictionary.mfaDialogContent.secondPoint.title}
          </Typography>
          <Typography sx={{whiteSpace: "pre-wrap", fontStyle: "italic"}}>
            {languageContext.dictionary.mfaDialogContent.secondPoint.text}
            {languageContext.dictionary.mfaDialogContent.secondPoint.errorContactPrefix}
            <Typography
              component={Button}
              color="secondary"
              href={`mailto:domain@invitech.hu`}
              sx={{paddingLeft: 0, paddingRight: 0, textTransform: "none"}}
            >
              {"domain@invitech.hu"}
            </Typography>
            {languageContext.dictionary.mfaDialogContent.secondPoint.errorContactSuffix}
          </Typography>
        </Box>
      </SimpleDialog>
      <LogoutDialog open={logoutDialogOpen} handleClose={() => setLogoutDialogOpen(false)}/>
    </div>
  );

  function setCookie(value, days) {
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * oneDayInMs));
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = cookieName + "=" + (value || "") + expires + "; path=/; domain=" + extractDomain(window.location.hostname);
  }

  function getCookie(name) {
    const nameEQ = name + "=";
    const cookies = document.cookie.split(';').map(cookie => cookie.trim());
    const targetCookie = cookies.find(cookie => cookie.startsWith(nameEQ));
    return targetCookie ? targetCookie.substring(nameEQ.length) : null;
  }

  function eraseCookie() {
    document.cookie = cookieName + '=; Max-Age=-99999999; path=/; domain=' + extractDomain(window.location.hostname);
  }

  function extractDomain(url) {
    return url?.replace(/^(?:https?:\/\/)?(?:[^\/]+\.)?([^.\/]+\.[^.\/]+).*$/, "$1");
  }
};

export default MfaDialog;
